import { motion } from "framer-motion";

const NODES = [
  { name: "eBay", mark: "e", bg: "#E53238" },
  { name: "Etsy", mark: "E", bg: "#F1641E" },
  { name: "Poshmark", mark: "P", bg: "#7F0353" },
  { name: "Mercari", mark: "M", bg: "#4DCAD5" },
  { name: "Depop", mark: "D", bg: "#FF2300" },
  { name: "Amazon", mark: "a", bg: "#FF9900" },
  { name: "Shopify", mark: "S", bg: "#96BF48" },
  { name: "Flipkart", mark: "Fk", bg: "#2874F0" },
  { name: "Grailed", mark: "G", bg: "#1A1A1A" },
];

const CX = 200;
const CY = 200;
const R = 142;
const HUB_R = 38;
const NODE_R = 20;
const ease = [0.22, 1, 0.36, 1];

function point(i, total, radius = R) {
  const a = (i / total) * Math.PI * 2 - Math.PI / 2;
  return { x: CX + radius * Math.cos(a), y: CY + radius * Math.sin(a) };
}

export default function HeroMarketScene({ rows = [] }) {
  return (
    <div className="relative h-full min-h-[64vh] lg:min-h-full overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(247,37,133,0.2),transparent_58%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(76,201,240,0.1),transparent_42%)]" />

      <div className="absolute inset-0 flex flex-col pt-16 pb-4 px-3 sm:px-5 lg:pt-[4.5rem] lg:pb-5">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.55, ease }}
          className="relative flex-1 min-h-0 flex items-center justify-center"
        >
          <svg
            viewBox="0 0 400 400"
            className="w-full h-full max-w-full max-h-full"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden
          >
            <defs>
              <linearGradient id="spoke" gradientUnits="userSpaceOnUse" x1="70" y1="70" x2="330" y2="330">
                <stop offset="0%" stopColor="#F72585" />
                <stop offset="100%" stopColor="#4CC9F0" />
              </linearGradient>
              <radialGradient id="hubFill" cx="35%" cy="30%" r="75%">
                <stop offset="0%" stopColor="#F72585" />
                <stop offset="100%" stopColor="#7B2CBF" />
              </radialGradient>
              <filter id="hubGlow" x="-40%" y="-40%" width="180%" height="180%">
                <feGaussianBlur stdDeviation="6" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {NODES.map((node, i) => {
              const p = point(i, NODES.length);
              return (
                <motion.path
                  key={`line-${node.name}`}
                  d={`M ${CX} ${CY} L ${p.x} ${p.y}`}
                  stroke="url(#spoke)"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 0.7 }}
                  transition={{ delay: 0.15 + i * 0.05, duration: 0.55, ease }}
                />
              );
            })}

            <motion.circle
              cx={CX}
              cy={CY}
              r="52"
              fill="none"
              stroke="rgba(247,37,133,0.35)"
              strokeWidth="1"
              animate={{ r: [52, 62, 52], opacity: [0.5, 0.15, 0.5] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            />

            {NODES.map((node, i) => {
              const p = point(i, NODES.length);
              return (
                <motion.g
                  key={node.name}
                  initial={{ opacity: 0, scale: 0.4 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.28 + i * 0.05, duration: 0.4, ease }}
                  style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                >
                  <motion.g
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 3 + (i % 4) * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.12 }}
                  >
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={NODE_R}
                      fill={node.bg}
                      stroke="rgba(255,255,255,0.18)"
                      strokeWidth="2"
                    />
                    <text
                      x={p.x}
                      y={p.y}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fill="#fff"
                      fontSize={node.mark.length > 1 ? 11 : 14}
                      fontWeight="700"
                      fontFamily="ui-sans-serif, system-ui, sans-serif"
                    >
                      {node.mark}
                    </text>
                    <text
                      x={p.x}
                      y={p.y + NODE_R + 12}
                      textAnchor="middle"
                      fill="rgba(255,255,255,0.5)"
                      fontSize="7"
                      letterSpacing="1.4"
                      fontFamily="ui-sans-serif, system-ui, sans-serif"
                    >
                      {node.name.toUpperCase()}
                    </text>
                  </motion.g>
                </motion.g>
              );
            })}

            <motion.g
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.12, duration: 0.5, ease }}
              style={{ transformOrigin: `${CX}px ${CY}px` }}
              filter="url(#hubGlow)"
            >
              <circle
                cx={CX}
                cy={CY}
                r={HUB_R}
                fill="url(#hubFill)"
                stroke="rgba(255,255,255,0.14)"
                strokeWidth="3"
              />
              <text
                x={CX}
                y={CY}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                fontSize="36"
                fontWeight="800"
                fontFamily="Georgia, 'Times New Roman', serif"
              >
                S
              </text>
            </motion.g>
          </svg>
        </motion.div>

        {rows.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.4, ease }}
            className="shrink-0 glass border border-white/10 px-4 py-2"
          >
            <div className="flex items-center justify-between text-[10px] tracking-luxe uppercase text-aura-muted mb-1">
              <span>Where to buy</span>
              <span className="text-aura-cyan">Best $119</span>
            </div>
            {rows.map((row) => (
              <div key={row.store} className="flex items-center justify-between py-1 border-t border-white/5 text-[13px]">
                <span className="text-white/85">{row.store}</span>
                <span className={`font-mono ${row.best ? "text-aura-cyan" : "text-white/70"}`}>
                  {row.best ? "★ " : ""}{row.price}
                </span>
              </div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
